import { useState, useEffect, useCallback } from 'react';

import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import orderBy from 'lodash/orderBy';
import throttle from 'lodash/throttle';
import without from 'lodash/without';

import {
  IAutoQuote,
  IAutoEnrollmentApplication,
  IQuoteApplicationHolder,
  SureError,
} from '../../interfaces';
import useExponentialBackoff from '../../services/useExponentialBackoff';
import useInterval from '../../services/useInterval';

const TRANSITION_DURATION = 300;

export type useAdpfProps = {
  getQuote: (quoteNumber: string) => Promise<IAutoQuote>;
  quote: IAutoQuote | null;
  handleGetQuoteError: (
    error: SureError,
    info?: { [key: string]: any }
  ) => void;
};

const adpfPendingStatus = ['pending', 'retrying'];
const adpfErrorStatus = ['failure', 'error'];

export function useAdpf({
  getQuote,
  quote,
  handleGetQuoteError,
}: useAdpfProps) {
  const [getQuoteRequestCount, setGetQuoteRequestCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const getQuoteInterval = useExponentialBackoff(getQuoteRequestCount, 4000);

  const sortedOrders = orderBy(
    get(quote, 'application.metadata.third_party_data_orders', []),
    ['order_date'],
    ['desc']
  );

  const latestAdpfOrder = find(sortedOrders, {
    data_type: 'adpf',
  });

  const isPending = adpfPendingStatus.includes(latestAdpfOrder?.status);

  const previousAdpfOrderWithError = find(
    without(sortedOrders, latestAdpfOrder),
    (order) =>
      order.data_type === 'adpf' && adpfErrorStatus.includes(order.status)
  );

  useInterval(function getQuoteUntilResolved() {
    if (!isSubmitting && isPending && !!quote) {
      setIsSubmitting(true);
      setGetQuoteRequestCount(
        (getQuoteRequestCount) => getQuoteRequestCount + 1
      );
      return getQuote(quote.quote_number)
        .then(() => {
          setIsSubmitting(false);
        })
        .catch((error) => {
          setIsSubmitting(false);
          handleGetQuoteError(error);
        });
    }
  }, getQuoteInterval);

  function getHasAddressError(errorList = []) {
    return !!errorList.filter((errorCode) =>
      includes(['invalid_address', 'unverifiable_address'], errorCode)
    ).length;
  }

  function getAdpfErrors() {
    if (adpfErrorStatus.includes(latestAdpfOrder?.status)) {
      const errorCodes = latestAdpfOrder.error_codes;

      const knockoutErrorCode = find(errorCodes, (errorCode: string) =>
        includes(
          [
            'equifax_unavailable',
            'frozen_credit',
            'high_risk',
            'household_knockout',
            'no_prior_coverage',
            'no_eligible_vehicles_on_quote',
            'unknown_error',
          ],
          errorCode
        )
      );

      const actionableErrorCodes = errorCodes.filter((errorCode: string) =>
        includes(
          [
            'invalid_address',
            'invalid_address_name',
            'invalid_dob',
            'invalid_name',
            'unverifiable_address',
            'invalid_name_and_address',
          ],
          errorCode
        )
      );

      const previousAdpfOrderErrorCodesSet = new Set(
        previousAdpfOrderWithError?.error_codes || []
      );
      const errorCodesSet = new Set(errorCodes || []);

      const hasIdenticalErrorCodesForBothOrders =
        !!previousAdpfOrderWithError &&
        isEqual(previousAdpfOrderErrorCodesSet, errorCodesSet);

      const hasAddressErrorCodesAfterHavingUnverifiableAddress =
        includes(
          previousAdpfOrderWithError?.error_codes || [],
          'unverifiable_address'
        ) && getHasAddressError(errorCodes);

      const valuesThatTriggerAdpfError = actionableErrorCodes.reduce(
        (values: Partial<IAutoEnrollmentApplication>, errorCode: string) => {
          const holder =
            find(quote?.application?.holders || [], {
              position_on_policy: 1,
            }) || ({} as IQuoteApplicationHolder);

          switch (errorCode) {
            case 'unverifiable_address':
            case 'invalid_address': {
              return {
                ...values,
                address: holder.address,
              };
            }
            case 'invalid_dob': {
              return {
                ...values,
                dob: holder.dob,
              };
            }
            case 'invalid_name': {
              return {
                ...values,
                firstName: holder.first_name,
                lastName: holder.last_name,
              };
            }
            case 'invalid_address_name': {
              return {
                ...values,
                firstName: holder.first_name,
                lastName: holder.last_name,
                address: holder.address,
              };
            }
            case 'invalid_name_and_address': {
              return {
                ...values,
                firstName: holder.first_name,
                lastName: holder.last_name,
                address: holder.address,
              };
            }
            default:
              return values;
          }
        },
        null
      );

      return {
        knockoutErrorCode,
        actionableErrorCodes,
        hasIdenticalErrorCodesForBothOrders,
        hasAddressErrorCodesAfterHavingUnverifiableAddress,
        valuesThatTriggerAdpfError,
      };
    }
    return {};
  }

  return {
    isOrderComplete: !isPending && !!latestAdpfOrder,
    isSubmitting,
    previousAdpfOrderWithError,
    getAdpfErrors,
  };
}

export function useAdpfLoadingView({
  isOrderComplete,
  onCompletion,
  numberOfChecklistItems,
}: {
  isOrderComplete: boolean;
  onCompletion: () => void;
  numberOfChecklistItems: number;
}) {
  const [progressBarValue, setProgressBarValue] = useState(0);
  const [completedChecklistItemCount, setCompletedChecklistItemCount] =
    useState(0);

  useEffect(
    function handleCompletion() {
      if (isOrderComplete && progressBarValue === 100) {
        const successTimeout = setTimeout(() => onCompletion(), 500);
        return () => {
          clearTimeout(successTimeout);
        };
      }
    },
    [isOrderComplete, onCompletion, progressBarValue]
  );

  const throttledSetProgress = throttle(
    (newValue) => setProgressBarValue(newValue),
    500
  );

  function handleUpdate(itemProgress: number) {
    const progressPerChecklistItem = 100 / numberOfChecklistItems;
    const valueOfCompletedItems =
      progressPerChecklistItem * completedChecklistItemCount;
    const valueOfThisItem = (itemProgress / 100) * progressPerChecklistItem;
    const newValue = valueOfCompletedItems + valueOfThisItem;
    newValue !== progressBarValue && throttledSetProgress(newValue);
  }

  function handleCompletion() {
    setCompletedChecklistItemCount(completedChecklistItemCount + 1);
  }

  return {
    progressBarValue,
    completedChecklistItemCount,
    handleUpdate,
    handleCompletion,
  };
}

export function useAdpfProgressDeterminate({
  intervalDelay,
  onUpdate,
  onCompletion,
  value,
  active,
}: {
  intervalDelay: number;
  onUpdate: (progressValue: number) => void;
  onCompletion: () => void;
  value: number;
  active: boolean;
}) {
  const [progressValue, setProgressValue] = useState(0);
  const [maxProgress, setMaxProgress] = useState(0);
  const [isFinished, setIsFinished] = useState(false);

  const optimizedOnUpdate = useCallback(onUpdate, []);

  useInterval(() => {
    if (active && maxProgress < 100) {
      setMaxProgress(maxProgress + 1);
    }
  }, intervalDelay);

  useEffect(() => {
    setProgressValue(value * (maxProgress / 100));
  }, [maxProgress, value]);

  useEffect(() => {
    optimizedOnUpdate(progressValue);
  }, [optimizedOnUpdate, progressValue]);

  useEffect(
    function handleCompletion() {
      if (progressValue >= 100 && !isFinished) {
        const interval = setTimeout(() => {
          setIsFinished(true);
          onCompletion();
        }, TRANSITION_DURATION);
        return () => {
          clearInterval(interval);
        };
      }
    },
    [isFinished, onCompletion, progressValue]
  );

  return {
    isFinished,
    progressValue,
  };
}
